/**
 * Client-side image downscaling for listing photos.
 *
 * Sellers attach photos straight from a phone camera — 3-5MB each, up to five
 * of them. Posting those raw put 15-25MB behind a single multipart request and
 * a 30s client timeout, which a mobile uplink cannot clear; the request was
 * aborted mid-body and the listing was never created. Downscaling here takes
 * that to a few hundred KB before it ever reaches the wire.
 *
 * The browser-only calls live behind an injected `deps` seam because jsdom
 * implements neither `createImageBitmap` nor canvas, so everything else in
 * this module is pure and directly testable.
 */

/** Long edge, in px, that a listing photo is scaled down to. */
export const MAX_EDGE = 1600;

/** JPEG quality for the re-encode. */
export const QUALITY = 0.82;

/** Below this, an in-bounds image is already small enough to send as-is. */
export const SKIP_BYTES = 400 * 1024;

/**
 * Refuse before decoding. Not a policy limit — a guard against a gigapixel
 * file exhausting memory in the tab.
 */
export const MAX_INPUT_BYTES = 25 * 1024 * 1024;

/** Ceiling on what we will send, checked against the compressed output. */
export const MAX_OUTPUT_BYTES = 5 * 1024 * 1024;

/** Mirrors the server's own 1-5 rule in apps/api/apps/cards/views.py. */
export const MAX_IMAGES = 5;

/**
 * Scale (w, h) to fit `maxEdge` on its long side, preserving aspect ratio.
 * Never upscales. Never returns a zero dimension — a zero-height canvas
 * throws on drawImage, which a wide-enough panorama would otherwise cause.
 */
export function targetDimensions(width, height, maxEdge = MAX_EDGE) {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxEdge) {
    return { width, height };
  }
  const scale = maxEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * True when `file` is already cheap enough to send untouched. Re-encoding a
 * photo that is small and in-bounds only costs it a generation of JPEG
 * quality for no bandwidth saved.
 */
export function shouldSkipReencode(file, width, height) {
  return file.size < SKIP_BYTES && Math.max(width, height) <= MAX_EDGE;
}

/**
 * Rewrite a filename's extension to .jpg, since everything we re-encode comes
 * back as JPEG. A leading dot is part of the name, not an extension, so
 * `.hidden` gains an extension rather than losing its name.
 */
export function toJpegName(name) {
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  return `${base}.jpg`;
}

/**
 * Return whichever of the original file or its re-encode is actually smaller.
 * JPEG is not universally smaller — a small flat-colour PNG routinely grows —
 * and shipping a bigger file for a quality loss is the worst of both.
 */
export function pickSmaller(originalFile, compressedBlob) {
  if (compressedBlob.size >= originalFile.size) {
    return originalFile;
  }
  return new File([compressedBlob], toJpegName(originalFile.name), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

/** Real canvas factory. Replaced by a fake in tests — jsdom has no canvas. */
function defaultMakeCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

/**
 * Downscale and re-encode `file`, returning a File no larger than the one
 * given. Returns the original unchanged whenever re-encoding would not help.
 *
 * Browser-only calls arrive through `deps` so this is exercisable under jsdom.
 */
export async function compressImage(file, deps = {}) {
  const {
    createImageBitmap: decode = (...args) => window.createImageBitmap(...args),
    makeCanvas = defaultMakeCanvas,
  } = deps;

  // `from-image` is load-bearing: drawing to a canvas strips the EXIF the
  // browser would otherwise have used to orient a phone photo.
  const bitmap = await decode(file, { imageOrientation: 'from-image' });

  try {
    const { width, height } = bitmap;
    if (shouldSkipReencode(file, width, height)) {
      return file;
    }

    const target = targetDimensions(width, height);
    const canvas = makeCanvas(target.width, target.height);
    const ctx = canvas.getContext('2d');

    // JPEG has no alpha channel, so an unpainted ground turns every
    // transparent PNG pixel black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, target.width, target.height);
    ctx.drawImage(bitmap, 0, 0, target.width, target.height);

    const blob = await canvasToBlob(canvas, 'image/jpeg', QUALITY);
    if (!blob) {
      return file;
    }
    return pickSmaller(file, blob);
  } finally {
    // Without this a five-photo batch holds five full-resolution decodes
    // alive at once, which is what exhausts memory on a mid-range phone.
    if (typeof bitmap.close === 'function') {
      bitmap.close();
    }
  }
}

const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png'];

function hasImageExtension(name) {
  const dot = name.lastIndexOf('.');
  return dot > 0 && ALLOWED_EXTENSIONS.includes(name.slice(dot).toLowerCase());
}

const asMB = (bytes) => (bytes / 1024 / 1024).toFixed(1);

/**
 * Validate and compress a batch of picked files.
 *
 * Returns the files to attach plus a refusal line per file that was dropped.
 * Ordering matters: a file is only checked against the remaining photo slots
 * once it is known to be a usable image, so an unusable one never costs the
 * seller a slot.
 */
export async function prepareImages(files, options = {}) {
  const { existingCount = 0, compress = compressImage } = options;
  const accepted = [];
  const refusals = [];
  let slots = MAX_IMAGES - existingCount;

  for (const file of files) {
    if (!hasImageExtension(file.name) || !file.type.startsWith('image/')) {
      refusals.push(`"${file.name}" is not a JPG, JPEG or PNG.`);
      continue;
    }

    if (file.size > MAX_INPUT_BYTES) {
      refusals.push(
        `"${file.name}" is ${asMB(file.size)}MB, which is too large to process. The limit is ${asMB(MAX_INPUT_BYTES)}MB.`
      );
      continue;
    }

    if (slots <= 0) {
      refusals.push(
        `"${file.name}" was not added — a listing can have at most ${MAX_IMAGES} photos.`
      );
      continue;
    }

    let prepared;
    try {
      // eslint-disable-next-line no-await-in-loop -- sequential on purpose:
      // decoding five full-res photos at once is what exhausts phone memory.
      prepared = await compress(file);
    } catch {
      // A failed decode must not cost the seller their photo. The upload is
      // larger than we wanted, but it is still very likely to succeed.
      prepared = file;
    }

    if (prepared.size > MAX_OUTPUT_BYTES) {
      refusals.push(
        `"${file.name}" is still ${asMB(prepared.size)}MB after compression and was skipped.`
      );
      continue;
    }

    accepted.push(prepared);
    slots -= 1;
  }

  return { accepted, refusals };
}
